import Iconify from 'src/components/Iconify'
import { styled } from '@mui/material/styles'
import { Stack, Box } from '@mui/material'
import { formatBytes } from 'helpers/formats/string'

type FileBoxProps = {
  disabled?: boolean
}

const FileBox = styled(Stack)<FileBoxProps>(
  ({ theme, disabled = false }) => ({
    outline: 'none',
    overflow: 'hidden',
    position: 'relative',
    padding: '24px 0px',
    borderRadius: theme.shape.borderRadius,
    transition: theme.transitions.create('padding'),
    border: `1px dashed ${theme.palette.grey[500_32]}`,
    backgroundColor: disabled ? 'rgba(141, 138, 156, 0.24)' : 'unset',
  }),
)

interface IProgressBarProps {
  label: string
  type?: string
  size?: number
  progress: number
  processing?: boolean
  isError?: boolean
  disabled?: boolean
  hiddenRetryBtn?: boolean
  onDelete(): void
  onRetry?(): void
}

interface IIconMapping {
  [key: string]: string
}

const iconMapping: IIconMapping = {
  video: 'bi:play-circle-fill',
  document: 'ic:baseline-summarize',
  asset: 'bx:folder',
  image: 'bi:image',
}

export default function ProgressBar({
  type = 'video',
  label,
  size = 0,
  progress = 0,
  processing = false,
  isError = false,
  disabled = false,
  hiddenRetryBtn = false,
  onDelete,
  onRetry = () => {},
}: IProgressBarProps) {
  return (
    <FileBox
      justifyContent="space-between"
      direction="row"
      disabled={disabled}
    >
      <Iconify
        icon={iconMapping[type]}
        sx={{
          margin: '0px 18px',
          alignSelf: 'center',
          minWidth: '27px',
          minHeight: '27px',
        }}
      />
      <Stack
        sx={{
          width: '100%',
          overflow: 'hidden',
          whiteSpace: 'wrap',
          textOverflow: 'ellipsis',
          overflowWrap: 'break-word',
        }}
        justifyContent="center"
      >
        <Box
          sx={{
            color: disabled ? '#3B95FF' : '#8D8A9C',
            fontWeight: '700',
            fontSize: '13px',
            lineHeight: '22px',
          }}
        >
          {label}
        </Box>
        {progress === 100 || isError ? (
          <>
            {!isError && (
              <Box
                sx={{
                  fontSize: '14px',
                  lineHeight: '22px',
                  color: '#6B6879',
                }}
              >
                {formatBytes(size)}
              </Box>
            )}
          </>
        ) : (
          <Box
            sx={{
              margin: '9px 0px',
              height: '4px',
              width: '100%',
              backgroundColor: 'rgba(145, 158, 171, 0.32)',
              borderRadius: '4px',
            }}
          >
            <Box
              sx={{
                height: '4px',
                width: `${progress}%`,
                backgroundColor: '#3B95FF',
                borderRadius: '4px',
              }}
            ></Box>
          </Box>
        )}
      </Stack>
      {(!disabled || !hiddenRetryBtn) && (
        <>
          {isError && (
            <Iconify
              icon="ant-design:reload-outlined"
              onClick={onRetry}
              sx={{
                color: '#CB1C4F',
                alignSelf: 'center',
                minWidth: '24px',
                minHeight: '24px',
              }}
            />
          )}
          <Iconify
            icon={
              progress === 100 || isError
                ? 'bxs:trash-alt'
                : 'iconoir:cancel'
            }
            onClick={() => {
              if (!processing) onDelete()
            }}
            sx={{
              margin: '0px 20px',
              color: processing ? 'grey' : '#CB1C4F',
              alignSelf: 'center',
              minWidth: '24px',
              minHeight: '24px',
            }}
          />
        </>
      )}
    </FileBox>
  )
}
